import { zodResolver } from "@hookform/resolvers/zod";
import {
  KikCustomerFlags,
  kikCustomerFlagsSchema,
  KikCustomerPersonalInfoFields,
  kikCustomerPersonalInfoSchema,
  KikUserZodSchemas,
} from "@kikocosmeticsorg/uc-api-nest-common-fe/zod";
import { SvcUpdateUser, UsersLoggedInUserDto } from "@springtree/eva-services-core";
import { omit, pick } from "lodash-es";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import React, { ReactElement, useEffect, useMemo, useRef } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import IconBestClients from "@/icons/icon-best-clients";
import IconBirthday from "@/icons/icon-birthday";
import IconDiamond from "@/icons/icon-diamond";
import IconGift from "@/icons/icon-gift";
import IconHelp from "@/icons/icon-help";
import Icon from "~/components/common/icon";
import myaccount from "~/components/common/myaccount.module.scss";
import Checkbox from "~/components/form/checkbox";
import ControlledPhoneInput from "~/components/form/controlled-phone-input";
import Errors from "~/components/form/errors";
import InputFieldDateMui from "~/components/form/input-field-date-mui";
import SelectGeneric from "~/components/form/select-generic";
import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import { useCountryDialog } from "~/contexts/country-dialog-context";
import { useEvaService } from "~/hooks/use-eva-service";
import useSubscriptions from "~/hooks/use-subscriptions";
import Logger from "~/services/logger/logger";
import CommonUtils from "~/utils/common-utils";
import crossCountryUtils from "~/utils/cross-country-utils";

import Button from "../../common/button";
import InputField from "../../form/input-field";
import styles from "./settings.module.scss";

type Props = {
  user: UsersLoggedInUserDto;
  onSubmitSuccess: () => void;
};

export default function PersonalInfoForm(props: Props) {
  const t = useTranslations();
  const router = useRouter();
  const breakpoint = useBreakpoint();
  const { updateUserProfile, authError, isLoading, refreshUserData } = useAuthContext();
  const {
    pushSubscriptionsByFlags,
    removeSubscriptionsByFlags,
    userSubscriptions,
    userSubscriptionsToFlags,
    availableSubscriptions,
  } = useSubscriptions();

  const updateUser = useEvaService(SvcUpdateUser);

  const { countries } = useCountryDialog();
  const availableLanguages = useMemo(
    () => crossCountryUtils.getAvailableUserLanguages(router, countries),
    [countries, router]
  );
  // const disabledLanguageSelect = availableLanguages.length < 2;
  const localizedCountryName = crossCountryUtils.getLocalizedCountryName(props.user.CountryID || "en", router);

  const {
    control,
    register,
    handleSubmit,
    clearErrors,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<KikCustomerPersonalInfoFields>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldFocusError: false,
    resolver: zodResolver(kikCustomerPersonalInfoSchema),
    defaultValues: {
      ...props.user,
    },
  });

  const onSubmit: SubmitHandler<KikCustomerPersonalInfoFields> = async (data) => {
    try {
      const flagKeys: (keyof KikCustomerFlags)[] = Object.keys(
        kikCustomerFlagsSchema.shape
      ) as (keyof KikCustomerFlags)[];
      // Get the modified flags only => Manage the subscriptions later
      const flags = pick(
        data,
        flagKeys.filter((k) => {
          return data[k as keyof KikCustomerFlags] !== optInCheckboxes[k as keyof KikCustomerFlags];
        })
      );
      // Exclude the flags (get all the rest) => Manage the update user
      const userData = omit(data, flagKeys);

      if (props.user?.ID) {
        await updateUser({
          ID: props.user.ID,
          ...userData,
        });
        await updateUserProfile(props.user.FirstName!, props.user.LastName!);
        await refreshUserData();
        await pushSubscriptionsByFlags(flags);
        await removeSubscriptionsByFlags(flags);
        props.onSubmitSuccess();
      }
    } catch (err: any) {
      Logger.instance.warn("Error caught", err);
    }
  };

  const refOptInCheckboxes = useRef<Partial<Record<keyof KikCustomerFlags, boolean | void>>>();
  const optInCheckboxes = userSubscriptionsToFlags();

  // Sync checkboxes appearance (checked/unchecked) with the real value of the associated privacy field
  useEffect(() => {
    if (
      !isLoading &&
      optInCheckboxes &&
      refOptInCheckboxes &&
      !CommonUtils.isEqual(optInCheckboxes, refOptInCheckboxes.current)
    ) {
      refOptInCheckboxes.current = optInCheckboxes;
      Object.entries(optInCheckboxes).forEach(([k, v]) => {
        setValue(k as keyof KikCustomerFlags, !!v);
      });
    }
  }, [optInCheckboxes, isLoading, refOptInCheckboxes, setValue]);

  const checkboxElements: ReactElement[] = [
    {
      key: "LoyaltyProgram" satisfies keyof KikCustomerFlags,
      element: (
        <div className={styles.loyaltyBox}>
          <Controller
            name={"LoyaltyProgram"}
            render={({ field }) => {
              return (
                <Checkbox
                  {...register("LoyaltyProgram")}
                  disabled={isLoading || !!optInCheckboxes?.LoyaltyProgram}
                  checked={field.value || false}
                  className={styles.privacyCheckbox}
                  label={t.rich("generic.signup_loyalty_program", {
                    br: () => <br />,
                    loyaltyParagraph: (chunks) => <p>{chunks}</p>,
                    strong: (chunks) => <strong>{chunks}</strong>,
                    benefitList: (chunks) => <ul className={styles.benefitList}>{chunks}</ul>,
                    benefit: (chunks) => <li className={styles.benefitItem}>{chunks}</li>,
                    benefitLabel: (chunks) => <span className={styles.benefitLabel}>{chunks}</span>,
                    svgIconServices: () => <Icon icon={IconHelp} className={styles.benefitIcon} />,
                    svgIconBirthdayGift: () => <Icon icon={IconBirthday} className={styles.benefitIcon} />,
                    svgIconWelcomeGift: () => <Icon icon={IconGift} className={styles.benefitIcon} />,
                    svgIconSpecialGifts: () => <Icon icon={IconBestClients} className={styles.benefitIcon} />,
                    svgIconVIPCare: () => <Icon icon={IconDiamond} className={styles.benefitIcon} />,
                  })}
                  error={errors.LoyaltyProgram?.message}
                />
              );
            }}
            control={control}
          />
        </div>
      ),
    },
    {
      key: "ProfilingOptIn" satisfies keyof KikCustomerFlags,
      element: (
        <>
          <p className={styles.profilingPurposesDisclaimer}>
            {t.rich("generic.profiling_purposes_with_link", {
              underline: (chunks) => <span className={styles.underline}>{chunks?.toString()}</span>,
            })}
          </p>
          <Controller
            name={"ProfilingOptIn"}
            render={({ field }) => {
              return (
                <Checkbox
                  {...register("ProfilingOptIn")}
                  disabled={isLoading}
                  checked={field.value || false}
                  className={styles.privacyCheckbox}
                  label={t("generic.online_profiling_opt_in")}
                  error={errors.ProfilingOptIn?.message}
                />
              );
            }}
            control={control}
          />
        </>
      ),
    },
    {
      key: "MarketingOptIn" satisfies keyof KikCustomerFlags,
      element: (
        <Controller
          name={"MarketingOptIn"}
          render={({ field }) => {
            return (
              <Checkbox
                {...register("MarketingOptIn")}
                disabled={isLoading}
                checked={field.value || false}
                className={styles.privacyCheckbox}
                label={t("generic.marketing_opt_in")}
                error={errors.MarketingOptIn?.message}
              />
            );
          }}
          control={control}
        />
      ),
    },
  ]
    .filter((item) => availableSubscriptions?.find((s) => s.BackendID === item.key))
    .map((item) => item.element);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.formGroup}>
        <h3 className={myaccount.titleXs}>{t("generic.myaccount.settings.personal_data")}</h3>
        <InputField
          {...register("FirstName")}
          type="text"
          label={t("generic.form.FirstName")}
          error={
            errors.FirstName?.message
              ? t(errors.FirstName?.message, {
                  fieldName: t("generic.form.FirstName"),
                  maxLength: KikUserZodSchemas.firstNameMaxlength,
                })
              : ""
          }
          onClearErrors={() => clearErrors("FirstName")}
          maxLength={KikUserZodSchemas.firstNameMaxlength}
        />
        <InputField
          {...register("LastName")}
          type="text"
          label={t("generic.form.LastName")}
          error={
            errors.LastName?.message
              ? t(errors.LastName?.message, {
                  fieldName: t("generic.form.LastName"),
                  maxLength: KikUserZodSchemas.lastNameMaxlength,
                })
              : ""
          }
          onClearErrors={() => clearErrors("LastName")}
          maxLength={KikUserZodSchemas.lastNameMaxlength}
        />
        <InputFieldDateMui
          control={control}
          name="DateOfBirth"
          label={t("generic.form.birthday")}
          error={
            errors.DateOfBirth?.message
              ? t(errors.DateOfBirth?.message, {
                  fieldName: t("generic.form.birthday"),
                  minimumAgeInYears: KikUserZodSchemas.userMinimumAgeInYears,
                  maximumAgeInYears: KikUserZodSchemas.userMaximumAgeInYears,
                })
              : ""
          }
          onClearErrors={() => clearErrors("DateOfBirth")}
          minimumDate={KikUserZodSchemas.userBirthdayMinDto}
          maximumDate={KikUserZodSchemas.userBirthdayMaxDto.minus({ day: 1 })}
        />
        <ControlledPhoneInput
          name="PhoneNumber"
          control={control}
          label={t("generic.form.PhoneNumber")}
          placeholder={t("generic.form.PhoneNumber_placeholder")}
          maxLength={KikUserZodSchemas.phoneNumberMaxlength}
          isOptional
          error={
            errors.PhoneNumber?.message
              ? t(errors.PhoneNumber?.message, {
                  fieldName: t("generic.form.PhoneNumber"),
                  maxLength: KikUserZodSchemas.phoneNumberMaxlength,
                  minLength: KikUserZodSchemas.phoneNumberMinlength,
                })
              : ""
          }
          onClearErrors={() => clearErrors("PhoneNumber")}
        />
      </div>
      <div className={styles.formGroup}>
        <h3 className={myaccount.titleXs}>{t("generic.myaccount.settings.country_language")}</h3>
        <InputField value={localizedCountryName} disabled type="text" label={t("generic.form.CountryID")} />
        <SelectGeneric
          {...register("LanguageID")}
          // disabled={disabledLanguageSelect}
          label={t("generic.form.language")}
          options={availableLanguages}
          error={errors.LanguageID?.message}
        />
      </div>
      <div className={styles.formGroup} style={{ display: "flex", flexDirection: "column", border: "none" }}>
        <h3 className={myaccount.titleXs}>{t("generic.myaccount.settings.personal_data")}</h3>
        {Array.isArray(userSubscriptions.user) ? (
          <>
            {checkboxElements.map((e, i) => {
              return <React.Fragment key={i}>{e}</React.Fragment>;
            })}
          </>
        ) : (
          <></>
        )}
      </div>

      <Errors error={authError} style={{ gridColumn: breakpoint === "desktop" ? "2 span" : "1" }} />

      <Button
        className={styles.settingsCta}
        disabled={isSubmitting}
        variant="secondary"
        type="submit"
        loading={isLoading}
      >
        {t("generic.myaccount.settings.save_cta")}
      </Button>
    </form>
  );
}
